/**
 * RGB Color Helper
 * Centralized utility functions for RGB color generation and management
 * Used across design components for consistent color theming
 */

/**
 * Checks if RGB color system is enabled for a given scale
 * @param {Object} scale - The scale object containing rgbColor property
 * @returns {boolean} - True if RGB colors are enabled, false otherwise
 */
export const isRgbColorEnabled = (scale) => {
  return scale?.rgbColor && scale.rgbColor !== "no-color";
};

/**
 * Converts hex color to RGB values
 * @param {string} hex - Hex color string (with or without #)
 * @returns {Object} - Object with r, g, b properties
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 123, b: 255 }; // Default to Bootstrap primary blue
};

/**
 * Converts RGB values to hex color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} - Hex color string
 */
const rgbToHex = (r, g, b) => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

/**
 * Generates color variations from a base RGB color
 * Creates primary, light, dark, and border color variations
 * @param {string} rgbColor - Base hex color string
 * @returns {Object} - Object with primary, light, dark, border color properties
 */
export const generateColorVariations = (rgbColor) => {
  // Return default Bootstrap colors if no RGB color or 'no-color' is specified
  if (!rgbColor || rgbColor === "no-color") {
    return {
      primary: "#007bff",
      light: "#e3f2fd",
      dark: "#0056b3",
      border: "#b3d9ff",
    };
  }

  const { r, g, b } = hexToRgb(rgbColor);

  // Generate color variations using mathematical formulas
  return {
    primary: rgbColor,
    // Light version: blend with white (80% towards white)
    light: rgbToHex(r + (255 - r) * 0.8, g + (255 - g) * 0.8, b + (255 - b) * 0.8),
    // Dark version: reduce intensity by 30%
    dark: rgbToHex(r * 0.7, g * 0.7, b * 0.7),
    // Border version: blend with white (50% towards white)
    border: rgbToHex(r + (255 - r) * 0.5, g + (255 - g) * 0.5, b + (255 - b) * 0.5),
  };
};

/**
 * Gets the appropriate color variations for a scale
 * Handles both RGB enabled and disabled states
 * @param {Object} scale - The scale object containing rgbColor property
 * @returns {Object} - Color variations object
 */
export const getScaleColors = (scale) => {
  const rgbEnabled = isRgbColorEnabled(scale);
  return generateColorVariations(rgbEnabled ? scale.rgbColor : "no-color");
};

/**
 * Default export object containing all helper functions
 */
const RgbColorHelper = {
  isRgbColorEnabled,
  generateColorVariations,
  getScaleColors,
};

export default RgbColorHelper;
